import { useCallback, useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminCreateStripePriceEntitlement,
  useAdminGetStripeProducts,
  getAdminGetStripePriceEntitlementsQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { createStripePriceSchema, type CreateStripePrice } from '@/schemas/stripe-price';
import { EntitlementRuleFields } from './EntitlementRuleFields';
import { logger } from '@/services/logger';
import {
  BILLING_SCENARIOS,
  BILLING_SCENARIO_LABELS,
  type BillingScenario,
} from '@/lib/billing-scenarios';

// Sentinel value for the "no scenario" option (Radix Select disallows empty string values).
const NO_SCENARIO = 'none';

/**
 * Props for the AddStripePriceDialog component
 */
interface AddStripePriceDialogProps {
  /** The product ID to associate with the Stripe price */
  productId: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional base price ID for add-on mode */
  basePriceId?: string;
  /** Optional Stripe product ID to pre-fill (used in add-on mode) */
  stripeProductId?: string;
}

/**
 * Dialog component for adding a new Stripe price mapping
 */
export function AddStripePriceDialog({
  productId,
  open,
  onOpenChange,
  basePriceId,
  stripeProductId: initialStripeProductId,
}: AddStripePriceDialogProps) {
  const queryClient = useQueryClient();
  const isAddOnMode = !!basePriceId;

  // Fetch Stripe products for selection
  const { data: productsData, isLoading: isLoadingProducts } = useAdminGetStripeProducts(
    { includeInactive: false },
    {
      query: {
        enabled: open, // Fetch synced products whenever the dialog is open (base and add-on modes)
      },
    }
  );

  const stripeProducts = productsData?.data?.products || [];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateStripePrice>({
    // The schema validates the generated polymorphic rule; cast bridges the resolver's input type.
    resolver: zodResolver(createStripePriceSchema) as Resolver<CreateStripePrice>,
    defaultValues: {
      stripePriceId: '',
      stripeProductId: initialStripeProductId ?? '',
      entitlementRule: { type: 'cumulative', count: 1 },
      basePriceId: basePriceId,
      scenario: null,
      isTierDefault: false,
      adjustableQuantity: false,
      minQuantity: 0,
      maxQuantity: null,
    },
  });

  const adjustableQuantity = watch('adjustableQuantity');
  const selectedStripeProductId = watch('stripeProductId');
  const scenario = watch('scenario');
  const isTierDefault = watch('isTierDefault');

  // Get available prices for the selected product
  const availablePrices = useMemo(() => {
    if (!selectedStripeProductId) return [];
    const product = stripeProducts.find((p) => p.stripeProductId === selectedStripeProductId);
    return product?.prices?.filter((p) => p.active) || [];
  }, [selectedStripeProductId, stripeProducts]);

  const { mutate: createStripePriceEntitlement } = useAdminCreateStripePriceEntitlement({
    mutation: {
      onSuccess: () => {
        toast.success('Stripe price mapping created successfully');

        // Invalidate queries to refresh the list
        queryClient.invalidateQueries({
          queryKey: getAdminGetStripePriceEntitlementsQueryKey(productId),
        });

        // Reset form and close dialog
        reset();
        onOpenChange(false);
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'CreateStripePriceEntitlement',
          productId,
        });
        toast.error(
          'Failed to create Stripe price mapping. ' +
            (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const onSubmit = useCallback(
    (data: CreateStripePrice) => {
      createStripePriceEntitlement({
        productId,
        data: {
          stripePriceId: data.stripePriceId,
          stripeProductId: data.stripeProductId,
          entitlementRule: data.entitlementRule,
          basePriceId: data.basePriceId,
          scenario: data.scenario ?? null,
          isTierDefault: data.isTierDefault,
          adjustableQuantity: data.adjustableQuantity,
          minQuantity: data.minQuantity,
          maxQuantity: data.maxQuantity,
        },
      });
    },
    [createStripePriceEntitlement, productId]
  );

  /**
   * Handle dialog close
   */
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isSubmitting) {
        reset();
      }
      onOpenChange(newOpen);
    },
    [isSubmitting, onOpenChange, reset]
  );

  /**
   * Handle product selection change - reset price selection
   */
  const handleProductChange = useCallback(
    (value: string) => {
      setValue('stripeProductId', value);
      setValue('stripePriceId', ''); // Reset price when product changes
    },
    [setValue]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isAddOnMode ? 'Add Add-On Price' : 'Add Stripe Price'}</DialogTitle>
          <DialogDescription>
            {isAddOnMode ? (
              <>
                Add an optional add-on price for the base price{' '}
                <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
                  {basePriceId}
                </code>
                . Customers can add this during checkout.
              </>
            ) : (
              'Link a Stripe price to this product. When a customer purchases this price, they will receive the configured entitlement.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-2">
            <Label htmlFor="stripeProductId">Stripe Product</Label>
            {isLoadingProducts ? (
              <Skeleton className="h-10 w-full" />
            ) : stripeProducts.length === 0 ? (
              <div className="text-sm text-muted-foreground p-2 border rounded-md">
                No products found. Sync products from Stripe first.
              </div>
            ) : (
              <Select value={selectedStripeProductId} onValueChange={handleProductChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a Stripe product" />
                </SelectTrigger>
                <SelectContent>
                  {stripeProducts.map((product) => (
                    <SelectItem key={product.stripeProductId} value={product.stripeProductId!}>
                      <div className="flex flex-col">
                        <span>{product.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {product.stripeProductId}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.stripeProductId && (
              <p className="text-sm text-destructive">{errors.stripeProductId.message}</p>
            )}
            {isAddOnMode && (
              <p className="text-sm text-muted-foreground">
                The add-on can use a different Stripe product than the base price.
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="stripePriceId">Stripe Price</Label>
            {!selectedStripeProductId ? (
              <div className="text-sm text-muted-foreground p-2 border rounded-md">
                Select a product first
              </div>
            ) : availablePrices.length === 0 ? (
              <div className="text-sm text-muted-foreground p-2 border rounded-md">
                No active prices found for this product
              </div>
            ) : (
              <Select
                value={watch('stripePriceId')}
                onValueChange={(value) => setValue('stripePriceId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a Stripe price" />
                </SelectTrigger>
                <SelectContent>
                  {availablePrices.map((price) => (
                    <SelectItem key={price.stripePriceId} value={price.stripePriceId!}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{price.stripePriceId}</span>
                        {price.hasEntitlementMapping && (
                          <span className="text-xs text-muted-foreground">(already mapped)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.stripePriceId && (
              <p className="text-sm text-destructive">{errors.stripePriceId.message}</p>
            )}
          </div>

          <EntitlementRuleFields
            setValue={setValue}
            watch={watch}
            fieldPath="entitlementRule"
            error={errors.entitlementRule?.message}
          />

          <div className="grid gap-2">
            <Label htmlFor="scenario">Billing scenario</Label>
            <Select
              value={scenario ?? NO_SCENARIO}
              onValueChange={(value) =>
                setValue('scenario', value === NO_SCENARIO ? null : (value as BillingScenario))
              }
            >
              <SelectTrigger id="scenario">
                <SelectValue placeholder="No scenario (unassigned)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SCENARIO}>No scenario (unassigned)</SelectItem>
                {BILLING_SCENARIOS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {BILLING_SCENARIO_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              The tier offering this price serves. Determines which organizations are shown it.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isTierDefault"
              checked={isTierDefault}
              onCheckedChange={(checked) => setValue('isTierDefault', checked === true)}
            />
            <Label htmlFor="isTierDefault" className="text-sm font-medium leading-none">
              Pre-selected default plan for its tier
            </Label>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="adjustableQuantity"
                checked={adjustableQuantity}
                onCheckedChange={(checked) => setValue('adjustableQuantity', checked === true)}
              />
              <Label htmlFor="adjustableQuantity" className="text-sm font-medium leading-none">
                Allow adjustable quantity at checkout
              </Label>
            </div>

            {adjustableQuantity && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div className="grid gap-2">
                  <Label htmlFor="minQuantity">Minimum quantity</Label>
                  <Input
                    id="minQuantity"
                    type="number"
                    min={0}
                    {...register('minQuantity', { valueAsNumber: true })}
                  />
                  {errors.minQuantity && (
                    <p className="text-sm text-destructive">{errors.minQuantity.message}</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="maxQuantity">Maximum quantity</Label>
                  <Input
                    id="maxQuantity"
                    type="number"
                    min={1}
                    placeholder="Unlimited"
                    {...register('maxQuantity', {
                      setValueAs: (v) => (v === '' || v === null ? null : parseInt(v, 10)),
                    })}
                  />
                  {errors.maxQuantity && (
                    <p className="text-sm text-destructive">{errors.maxQuantity.message}</p>
                  )}
                  <p className="text-sm text-muted-foreground">Leave blank for unlimited</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : isAddOnMode ? 'Add Add-On' : 'Add Price'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
